<template>
  <v-container>
    <div class="title-icon">
      <h1>{{ $t("company.sections") }}</h1>
      <v-btn
        icon
        :href="links.main.departments"
        target="_blank"
      >
        <v-icon>$sl-question</v-icon>
      </v-btn>
    </div>

    <p>
      {{ $t("company.dep-description") }}
    </p>

    <v-text-field
      class="search-field mb-4 mt-5"
      prepend-icon="$sl-search"
      single-line
      hide-details
      :label="$t('application.search-label')"
      @input="init"
      v-model="search"
    ></v-text-field>
    <MessageBox type="info" class="mb-8" v-if="!isAdministrator">
      <p>
        {{ $t("company.department-messagebox") }}
        {{ getCurrentUserDepartments($route.params.id, isAdministrator) }}
      </p>
    </MessageBox>
    <v-expand-transition v-if="searched && searched.length > 0">
      <v-expansion-panels class="mt-0" v-model="panel">
        <v-expansion-panel v-for="(dep, index) in searched" :key="index">
          <v-expansion-panel-header expand-icon="$sl-arrow-down" two-line>
            <div>
              <div class="header-title no-text-transform" v-if="dep && dep.name">
                {{ dep.name }}
              </div>
              <div class="subtitle-2 gray d-flex">
                <div v-if="dep.instructors">{{ dep.instructors.length + " HL" }}</div>
                <div v-else>0 HL</div>
                <div class="pl-2" v-if="dep.companyEducationResponsibleIds">
                  {{ dep.companyEducationResponsibleIds.length + " YUA" }}
                </div>
                <div class="pl-2" v-else>0 YUA</div>
                <div class="pl-2" v-if="dep.apprentices">
                  {{ dep.apprentices.length + " LÄR" }}
                </div>
                <div class="pl-2" v-else>0 LÄR</div>
                <div class="pl-2" v-if="dep.departmentWorkplaces">
                  {{ dep.departmentWorkplaces.length + " AP" }}
                </div>
                <div class="pl-2" v-else>0 AP</div>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="caption font-italic" v-if="dep.description">
              {{ dep.description }}
            </p>

            <ActionButton
              @click="
                $nextTick(() => {
                  $router.push({
                    name: 'CompanyEmployees',
                    params: { id: $route.params.id, tab: 1, search: dep.name },
                  });
                })
              "
              type="application"
              actionIcon="$sl-meeting"
              >{{ $t("company.show-staff") }}</ActionButton
            >
            <ActionButton
              @click="
                $nextTick(() => {
                  $router.push({
                    name: 'CompanyWorkplaces',
                    params: { id: $route.params.id, tab: 1, search: dep.name },
                  });
                })
              "
              type="application mt-4"
              actionIcon="$sl-workplace"
              >{{ $t("company.show-wp") }}</ActionButton
            >
            <ActionButton
              @click="
                $nextTick(() => {
                  $router.push({
                    name: 'Department',
                    params: { id: $route.params.id, departmentId: dep.id },
                  });
                })
              "
              type="application mt-4"
              actionIcon="$sl-write"
              >{{ $t("labels.change") }}</ActionButton
            >
            <ActionButton
              @click="showDeleteModal(dep)"
              type="application mt-4 mb-1"
              actionIcon="$sl-delete"
              :isDisabled="isImitatingUser()"
              >{{ $t("labels.delete") }}</ActionButton
            >
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-expand-transition>
    <p class="overline small">{{ $t("company.legend") }}</p>

    <div class="caption mt-n2">
      {{ $t("company.desc-instructor") }}
    </div>
    <div class="caption mt-1">
      {{ $t("company.desc-yua") }}
    </div>
    <div class="caption mt-1">
      {{ $t("company.desc-apprentice") }}
    </div>
    <div class="caption mt-1">
      {{ $t("company.desc-wp") }}
    </div>
    <v-btn
      fixed
      fab
      bottom
      right
      class="elevation"
      color="primary"
      :disabled="!isAdministrator || isImitatingUser()"
      @click="
        $nextTick(() => {
          $router.push({
            name: 'Department',
            params: { id: $route.params.id, departmentId: 0 },
          });
        })
      "
    >
      <v-icon>$sl-add</v-icon>
    </v-btn>

    <ModalBase
        :show="showModal"
        maxWidth="400px"
        :title="$t('company.delete-department')"
        cancelIcon="$sl-cancel"
        submitIcon="$sl-delete"
        cancelText="Avbryt"
        submitText="Radera"
        @close="closeModal"
        @cancel="closeModal"
        @submit="deleteDep"
        :outlinedCancel="false"
    />
  </v-container>
</template>

<script>
import API from "@/services/api.js";
import { CompanyRoles } from "@/components/mixins";
import { mapGetters } from "vuex";
import MessageBox from "@/components/Common/MessageBox";
import ActionButton from "@/components/Common/ActionButton";
import ModalBase from '@/components/Common/ModalBase';
import ModalBaseMixin from "@/components/mixins/ModalBaseMixin";

const SugarDate = require("sugar/date");

export default {
  name: "CompanyDepartments",

  mixins: [CompanyRoles, ModalBaseMixin],

  components: {
    ActionButton,
    MessageBox,
    ModalBase,
  },

  data: () => ({
    links: require('../../lib/external-links.json'),
    search: "",
    panel: [1],
    tab: null,
    departments: [],
    selectedPerson: {
      id: -1,
    },
    sections: [], //Avdelningar
  }),

  created() {
    this.init();
  },

  computed: {
    ...mapGetters("currentuser", ["currentUser", "isInstructor", "isAdministrator", "isImitatingUser"]),
    /**
     * Used get all exemptions or search exemptions if search is required from user
     * @arg called with no is a string comming from search bar
     */
    searched() {
      let result = [];
      if (this.search === "") {
        result = this.departments;
      } else {
        let normalizedSearch = this.search.toLowerCase();

        result = this.departments.filter(
          (x) => x.name && x.name.toLowerCase().includes(normalizedSearch)
          //|| (x.book.person.lastName && x.book.person.lastName.toLowerCase().includes(normalizedSearch)
        );
      }
      return result;
    },
  },

  methods: {
    /**
     * @vuese
     * Used to call method  getApprenticeById or  getEmployeeById based on route name
     * @arg called with no argument
     */
    init() {
      this.listDepartments();
    },
    /**
     * @vuese
     * Used to delete a file from files array
     * @arg The argument is an integer value representing index
     */
    async deleteDep() {
      this.showModal = false;
      if (!this.selectedItem) return;

      API.deleteDepartment(this.$route.params.id, this.selectedItem.id)
        .then((response) => {
          this.panel = [];
          //this.departments = response.data
          this.departments.splice(this.departments.indexOf(this.selectedItem), 1);
          this.$events.fire("showInfo", this.$t("success.deleted"));
          //this.departments = response.data.filter(x => x.approvedBySecondaryId && x.approvedBySecondaryId > 0)
        })
        .catch((error) => {
          this.$events.fire("showError", this.$t("company.delete-department-error"));
          console.log("There was an error:", error);
        });
      // this.selectedFiles.splice(this.selectedFiles.indexOf(item), 1)
      // this.selectedFiles = [...this.selectedFiles]
      // this.files = {}
      // this.$refs.uploadForm.reset()
    },
    /**
     * @vuese
     * Used to get the list of paused employees
     * @arg The argument is an integer value representing company id
     */
    listDepartments() {
      if (!(this.$route && this.$route.params && this.$route.params.id)) return;

      API.listDepartments(this.$route.params.id)
        .then((response) => {
          if (this.isAdministrator) {
            this.departments = response.data;                
          } else {
            let depIds = this.getCurrentUserDepartmentsIds(
              this.$route.params.id,
              false,
              true
            );
            this.departments = response.data.filter((r) => depIds.find((u) => u == r.id));            
          }
        })
        .catch((error) => {
          console.log("There was an error:", error);
        });
    },
    /**
     * @vuese
     * Used to edit the pause period
     * @arg The argument is
     */
    editClick(emp) {
      this.selectedPerson = emp;
    },
  },
};
</script>
<style scoped></style>
